import React, { useState, useContext, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { getToken, URL } from "../../utils";
import { toast, ToastContainer } from "react-toastify";
import Select from "react-select";
import { UserContext } from "../../contexts/UserContext";

export default function ParticularAddFacture() {
  const [formData, setFormData] = useState({
    nom: "",
    prenom: "",
    adresse: "",
    dateNaissance: "",
    contact1: "",
    contact2: "",
    email: "",
    is_father: false,
    is_mother: false,
    is_grand_father: false,
    is_grand_mother: false,
    nationalite: "TG",
    sexe: "M",
    magasin: null,
    commercial: null,
    at_event: false,
    name_event: "",
  });

  const [magasinOptions, setMagasinOptions] = useState([]);
  const [commercialOptions, setCommercialOptions] = useState([]);
  const [loading, setLoading] = useState({
    magasins: false,
    commercials: false,
  });
  const [validationErrors, setValidationErrors] = useState({});

  const navigate = useNavigate();
  const token = getToken();
  const { user } = useContext(UserContext);

  // Gestion cohérente des types de clients
  const handleTypeChange = (e) => {
    const { name, checked } = e.target;

    setFormData((prev) => {
      const newData = {
        ...prev,
        [name]: checked,
      };

      // Empêcher la sélection croisée entre genres
      if (checked) {
        if (name === "is_father" || name === "is_grand_father") {
          newData.is_mother = false;
          newData.is_grand_mother = false;
        }
        if (name === "is_mother" || name === "is_grand_mother") {
          newData.is_father = false;
          newData.is_grand_father = false;
        }
      }
      return newData;
    });
  };

  useEffect(() => {
    // Charger les magasins et commerciaux au montage du composant
    const loadData = async () => {
      if (!user.is_caissier) {
        setLoading((prev) => ({ ...prev, magasins: true }));
        const magasins = await fetchData();
        setMagasinOptions(magasins);
        setLoading((prev) => ({ ...prev, magasins: false }));

        setLoading((prev) => ({ ...prev, commercials: true }));
        const commercials = await fetchCommercials();
        setCommercialOptions(commercials);
        setLoading((prev) => ({ ...prev, commercials: false }));
      }
    };

    loadData();
  }, [user.is_caissier]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleSelectChange = (name, selectedOption) => {
    setFormData((prev) => ({
      ...prev,
      [name]: selectedOption ? selectedOption.value : null,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setValidationErrors({});

    const newClient = {
      nom: formData.nom,
      prenom: formData.prenom,
      contact1: formData.contact1,
      contact2: formData.contact2,
      adresse: formData.adresse,
      date_naissance: formData.dateNaissance,
      email: formData.email,
      is_father: formData.is_father,
      is_mother: formData.is_mother,
      is_grand_father: formData.is_grand_father,
      is_grand_mother: formData.is_grand_mother,
      magasin: formData.magasin,
      commercial: formData.commercial,
      at_event: formData.at_event,
      name_event: formData.name_event,
    };

    try {
      const response = await axios.post(
        URL + "Clients/create-client-particulier",
        newClient,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      if (response.status === 201) {
        toast.success("Client ajouté avec succès");
        navigate("/clients/particuliers");
      }
    } catch (err) {
      if (err.response && err.response.data) {
        setValidationErrors(err.response.data);
        if (err.response.status === 400) {
          toast.error("Veuillez corriger les erreurs du formulaire");
        } else if (err.response.status === 401) {
          toast.error("Session expirée, veuillez vous reconnecter");
          navigate("/login");
        } else if (err.response.status === 500) {
          toast.error("Erreur serveur, veuillez réessayer plus tard");
        }
      } else {
        console.error("Erreur lors de l'ajout du client:", err);
        toast.error("Une erreur est survenue lors de l'ajout du client");
      }
    }
  };

  const fetchData = async () => {
    try {
      const response = await axios.get(URL + "Magasins/magasins-list", {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      return response.data.map((magasin) => ({
        value: magasin.id,
        label: `${magasin.nom} - ${magasin.quartier}`,
      }));
    } catch (err) {
      console.error("Erreur lors de la récupération des magasins:", err);
      toast.error("Erreur lors du chargement des magasins");
      return [];
    }
  };

  const fetchCommercials = async () => {
    try {
      const response = await axios.get(
        URL + "Magasins/commercial/liste-commerciaux",
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      return response.data.map((commercial) => ({
        value: commercial.id,
        label: `${commercial.nom} - ${commercial.prenom}`,
      }));
    } catch (err) {
      console.error("Erreur lors de la récupération des commerciaux:", err);
      toast.error("Erreur lors du chargement des commerciaux");
      return [];
    }
  };

  // Style personnalisé pour les cases à cocher avec gestion cohérente
  const CheckboxField = ({ name, label, description = "" }) => (
    <div className="flex items-center mb-4">
      <input
        type="checkbox"
        id={name}
        name={name}
        checked={formData[name]}
        onChange={handleTypeChange} // Utilisation de handleTypeChange au lieu de handleChange
        className="w-4 h-4 text-amber-600 bg-gray-100 border-gray-300 rounded focus:ring-amber-500 focus:ring-2"
      />
      <label htmlFor={name} className="ml-2 text-sm font-medium text-gray-900">
        {label}
        {description && <p className="text-xs text-gray-500">{description}</p>}
      </label>
      {validationErrors[name] && (
        <p className="ml-2 text-red-500 text-sm">{validationErrors[name]}</p>
      )}
    </div>
  );

  return (
    <div className="p-4 w-[min(600px,90%)] mx-auto mt-4 bg-white shadow-md rounded-md">
      <ToastContainer position="top-right" autoClose={3000} />
      <div className="flex gap-4 p-4 pb-0">
        <div className="p-3 bg-amber-200 rounded-full text-amber-600 grid justify-center items-center">
          <svg
            className="w-8 h-8"
            aria-hidden="true"
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            fill="none"
            viewBox="0 0 24 24"
          >
            <path
              stroke="currentColor"
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              d="M16 12h4m-2 2v-4M4 18v-1a3 3 0 0 1 3-3h4a3 3 0 0 1 3 3v1a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1Zm8-10a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z"
            />
          </svg>
        </div>
        <div className="">
          <h1 className="text-lg font-semibold">Ajouter un nouveau client</h1>
          <p className="text-sm text-gray-500">
            Remplissez le formulaire pour ajouter un nouveau particulier.
          </p>
        </div>
      </div>
      <form className="p-4" onSubmit={handleSubmit}>
        <div className="mb-4">
          <label
            htmlFor="nom"
            className="block mb-2 text-sm font-medium text-gray-900"
          >
            Nom <span className="text-red-500 text-xl font-bold pl-1">*</span>
          </label>
          <input
            type="text"
            id="nom"
            name="nom"
            value={formData.nom}
            onChange={handleChange}
            className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-amber-500 focus:border-amber-500 block w-full p-2.5"
            placeholder="Nom"
            required
          />
          {validationErrors.nom && (
            <p className="text-red-500 text-sm">{validationErrors.nom}</p>
          )}
        </div>

        <div className="mb-4">
          <label
            htmlFor="prenom"
            className="block mb-2 text-sm font-medium text-gray-900"
          >
            Prénom
          </label>
          <input
            type="text"
            id="prenom"
            name="prenom"
            value={formData.prenom}
            onChange={handleChange}
            className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-amber-500 focus:border-amber-500 block w-full p-2.5"
            placeholder="Prénom"
          />
          {validationErrors.prenom && (
            <p className="text-red-500 text-sm">{validationErrors.prenom}</p>
          )}
        </div>

        <div className="flex gap-4 mb-4 max-sm:flex-col">
          <div className="flex-grow sm:w-1/2">
            <label
              htmlFor="adresse"
              className="block mb-2 text-sm font-medium text-gray-900"
            >
              Adresse
            </label>
            <input
              type="text"
              id="adresse"
              name="adresse"
              value={formData.adresse}
              onChange={handleChange}
              className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-amber-500 focus:border-amber-500 block w-full p-2.5"
              placeholder="Lométokoin"
            />
            {validationErrors.adresse && (
              <p className="text-red-500 text-sm">{validationErrors.adresse}</p>
            )}
          </div>
          <div className="flex-grow sm:w-1/2">
            <label
              htmlFor="dateNaissance"
              className="block mb-2 text-sm font-medium text-gray-900"
            >
              Date de naissance
            </label>
            <input
              type="date"
              id="dateNaissance"
              name="dateNaissance"
              value={formData.dateNaissance}
              onChange={handleChange}
              className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-amber-500 focus:border-amber-500 block w-full p-2.5"
            />
            {validationErrors.date_naissance && (
              <p className="text-red-500 text-sm">
                {validationErrors.date_naissance}
              </p>
            )}
          </div>
        </div>

        <div className="flex gap-4 mb-4 max-sm:flex-col">
          <div className="flex-grow sm:w-1/2">
            <label
              htmlFor="contact1"
              className="block mb-2 text-sm font-medium text-gray-900"
            >
              Contact 1
              <span className="text-red-500 text-xl font-bold pl-1">*</span>
            </label>
            <input
              type="tel"
              id="contact1"
              name="contact1"
              value={formData.contact1}
              onChange={handleChange}
              className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-amber-500 focus:border-amber-500 block w-full p-2.5"
              placeholder="+xxx xxxxxxxx"
              required
            />
            {validationErrors.contact1 && (
              <p className="text-red-500 text-sm">
                {validationErrors.contact1}
              </p>
            )}
          </div>
          <div className="flex-grow sm:w-1/2">
            <label
              htmlFor="contact2"
              className="block mb-2 text-sm font-medium text-gray-900"
            >
              Contact 2
            </label>
            <input
              type="tel"
              id="contact2"
              name="contact2"
              value={formData.contact2}
              onChange={handleChange}
              className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-amber-500 focus:border-amber-500 block w-full p-2.5"
              placeholder="+xxx xxxxxxxx"
            />
            {validationErrors.contact2 && (
              <p className="text-red-500 text-sm">
                {validationErrors.contact2}
              </p>
            )}
          </div>
        </div>

        <div className="mb-4">
          <label
            htmlFor="email"
            className="block mb-2 text-sm font-medium text-gray-900"
          >
            Email
          </label>
          <input
            type="email"
            id="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-amber-500 focus:border-amber-500 block w-full p-2.5"
            placeholder="exemple@gmail.com"
          />
          {validationErrors.email && (
            <p className="text-red-500 text-sm">{validationErrors.email}</p>
          )}
        </div>

        {/* Section Magasin et Commercial */}
        {!user.is_caissier && (
          <>
            <div className="mb-4">
              <label className="block mb-2 text-sm font-medium text-gray-900">
                Magasin{" "}
                <span className="text-red-500 text-xl font-bold pl-1">*</span>
              </label>
              <Select
                options={magasinOptions}
                isLoading={loading.magasins}
                onChange={(selectedOption) =>
                  handleSelectChange("magasin", selectedOption)
                }
                placeholder="Sélectionner un magasin..."
                noOptionsMessage={() => "Aucun résultat"}
                isClearable
                styles={{
                  control: (base) => ({
                    ...base,
                    borderColor: "#d1d5db",
                    boxShadow: "none",
                    "&:hover": {
                      borderColor: "#f59e0b",
                    },
                  }),
                }}
              />
              {validationErrors.magasin && (
                <p className="text-red-500 text-sm">
                  {validationErrors.magasin}
                </p>
              )}
            </div>
          </>
        )}
        <div className="mb-4">
          <label className="block mb-2 text-sm font-medium text-gray-900">
            Commercial
          </label>
          <Select
            options={commercialOptions}
            isLoading={loading.commercials}
            onChange={(selectedOption) =>
              handleSelectChange("commercial", selectedOption)
            }
            placeholder="Sélectionner un commercial..."
            noOptionsMessage={() => "Aucun résultat"}
            isClearable
            styles={{
              control: (base) => ({
                ...base,
                borderColor: "#d1d5db",
                boxShadow: "none",
                "&:hover": {
                  borderColor: "#f59e0b",
                },
              }),
            }}
          />
          {validationErrors.commercial && (
            <p className="text-red-500 text-sm">
              {validationErrors.commercial}
            </p>
          )}
        </div>

        {/* Section des cases à cocher améliorées */}
        <div className="mb-4 p-4 border border-gray-200 rounded-lg">
          <h3 className="mb-3 text-sm font-medium text-gray-900">
            Type de client
          </h3>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <CheckboxField
              name="is_father"
              label="Client père"
              description="Cocher si le client est un père"
            />
            <CheckboxField
              name="is_mother"
              label="Client mère"
              description="Cocher si le client est une mère"
            />
            <CheckboxField
              name="is_grand_father"
              label="Client grand-père"
              description="Cocher si le client est un grand-père"
            />
            <CheckboxField
              name="is_grand_mother"
              label="Client grand-mère"
              description="Cocher si le client est une grand-mère"
            />
          </div>
        </div>

        {/* Sexe */}
        <div className="mb-4">
          <label className="block mb-2 text-sm font-medium text-gray-900">
            Sexe
          </label>
          <div className="flex items-center gap-4">
            <div className="flex items-center">
              <input
                type="radio"
                id="sexe_masculin"
                name="sexe"
                value="M"
                checked={formData.sexe === "M"}
                onChange={handleChange}
                className="w-4 h-4 text-amber-600 bg-gray-100 border-gray-300 focus:ring-amber-500 focus:ring-2"
              />
              <label
                htmlFor="sexe_masculin"
                className="ml-2 text-sm font-medium text-gray-900"
              >
                Masculin
              </label>
            </div>
            <div className="flex items-center">
              <input
                type="radio"
                id="sexe_feminin"
                name="sexe"
                value="F"
                checked={formData.sexe === "F"}
                onChange={handleChange}
                className="w-4 h-4 text-amber-600 bg-gray-100 border-gray-300 focus:ring-amber-500 focus:ring-2"
              />
              <label
                htmlFor="sexe_feminin"
                className="ml-2 text-sm font-medium text-gray-900"
              >
                Féminin
              </label>
            </div>
          </div>
          {validationErrors.sexe && (
            <p className="text-red-500 text-sm">{validationErrors.sexe}</p>
          )}
        </div>

        {/* Select for nationality (use reacte select) */}
        <div className="mb-4">
          <label
            htmlFor="nationalite"
            className="block mb-2 text-sm font-medium text-gray-900"
          >
            Nationalité
          </label>
          <Select
            options={[
              { value: "CI", label: "Ivoirienne" },
              { value: "BJ", label: "Béninoise" },
              { value: "NE", label: "Nigerienne" },
              { value: "ML", label: "Malienne" },
              { value: "SN", label: "Sénégalaise" },
              { value: "BF", label: "Burkinabé" },
              { value: "FR", label: "Française" },
              { value: "US", label: "Américaine" },
              { value: "NG", label: "Nigériane" },
              { value: "CM", label: "Camerounaise" },
              { value: "GA", label: "Gabonaise" },
              { value: "CD", label: "Congolaise" },
              { value: "CG", label: "Congolaise (Brazzaville)" },
              { value: "ML", label: "Malienne" },
              { value: "CI", label: "Ivoirienne" },
              { value: "GN", label: "Guinéenne" },
              { value: "TG", label: "Togolaise" },
              { value: "DZ", label: "Algérienne" },
              { value: "MA", label: "Marocaine" },
              { value: "TN", label: "Tunisienne" },
              { value: "LY", label: "Libyenne" },
              { value: "EG", label: "Égyptienne" },
              { value: "ZA", label: "Sud-Africaine" },
              { value: "ZM", label: "Zambienne" },
              { value: "ZW", label: "Zimbabwéenne" },
              { value: "KE", label: "Kényane" },
              { value: "UG", label: "Ougandaise" },
              { value: "RW", label: "Rwandaise" },
              { value: "TZ", label: "Tanzanienne" },
              { value: "MW", label: "Malawienne" },
              { value: "AO", label: "Angolaise" },
              { value: "MZ", label: "Mozambicaine" },
              { value: "SC", label: "Seychelloise" },
              { value: "MU", label: "Mauricienne" },
              { value: "LR", label: "Libérienne" },
              { value: "SL", label: "Sierra-Léonaise" },
              { value: "GH", label: "Ghanaienne" },
              { value: "BJ", label: "Béninoise" },
            ]}
            value={{
              value: formData.nationalite,
              label:
                formData.nationalite === "TG"
                  ? "Togolaise"
                  : formData.nationalite === "FR"
                  ? "Française"
                  : "Américaine",
            }}
            onChange={(selectedOption) =>
              handleSelectChange("nationalite", selectedOption)
            }
            placeholder="Sélectionner une nationalité..."
            noOptionsMessage={() => "Aucun résultat"}
            isClearable
            styles={{
              control: (base) => ({
                ...base,
                borderColor: "#d1d5db",
                boxShadow: "none",
                "&:hover": {
                  borderColor: "#f59e0b",
                },
              }),
            }}
          />
          {validationErrors.nationalite && (
            <p className="text-red-500 text-sm">
              {validationErrors.nationalite}
            </p>
          )}
        </div>

        {/* Section événement */}
        <div className="mb-4 p-4 border border-gray-200 rounded-lg">
          <CheckboxField name="at_event" label="Est-ce lors d'un événement ?" />

          {formData.at_event && (
            <div className="mt-3">
              <label
                htmlFor="name_event"
                className="block mb-2 text-sm font-medium text-gray-900"
              >
                Nom de l'événement
              </label>
              <input
                type="text"
                id="name_event"
                name="name_event"
                value={formData.name_event}
                onChange={handleChange}
                className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-amber-500 focus:border-amber-500 block w-full p-2.5"
                placeholder="Nom de l'événement"
              />
              {validationErrors.name_event && (
                <p className="text-red-500 text-sm">
                  {validationErrors.name_event}
                </p>
              )}
            </div>
          )}
        </div>

        <div className="grid grid-cols-2 items-center gap-4 mt-6">
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="hover:text-white border border-gray-800 bg-gray-200 hover:bg-gray-900 focus:outline-none focus:ring-4 focus:ring-gray-300 font-medium rounded-lg text-sm px-5 py-2.5"
          >
            Annuler
          </button>
          <button
            type="submit"
            className="text-white bg-amber-700 hover:bg-amber-800 focus:ring-4 focus:outline-none focus:ring-amber-300 font-medium rounded-lg text-sm w-full sm:w-auto px-5 py-2.5 text-center"
          >
            Ajouter
          </button>
        </div>
      </form>
    </div>
  );
}
